import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const SITE = "https://tkshopmy.com";

const STATIC_TAG_SELECTOR = [
  'meta[name="description"]',
  'link[rel="canonical"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:card"]',
].join(",");

export function Seo({ title, description, path = "/", schemas = [], noindex = false }) {
  useEffect(() => {
    // index.html ships static homepage meta; Helmet appends per-page tags after
    // them. Dedupe by keeping the last occurrence (Helmet's) for each selector.
    const t = setTimeout(() => {
      STATIC_TAG_SELECTOR.split(",").forEach((sel) => {
        const els = Array.from(document.head.querySelectorAll(sel));
        els.slice(0, -1).forEach((el) => el.remove());
      });
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const url = `${SITE}${path}`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="TKShopMY" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-8" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1.5 text-xs text-ink/50">
        {items.map((it, i) => (
          <li key={it.path} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight size={12} className="text-ink/30" />}
            {i === items.length - 1 ? (
              <span className="text-ink/70 font-medium" aria-current="page">{it.name}</span>
            ) : (
              <Link to={it.path} className="hover:text-forest transition-colors">{it.name}</Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
